import { memo, useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogBody } from '../catalyst/dialog';
import { Badge } from '../catalyst/badge';
import { Close } from '@carbon/icons-react';
import { cn } from '../../utils';
import {
  getFileTypeIcon,
  isImageType,
  isPdfType,
  isTextType,
  formatFileSize,
} from '../../utils/attachments';
import { LoadingSpinner } from './loading-spinner';
import type { FileUIPart } from '@sharpe/shared/server';
import { PdfViewer } from './pdf-components';

interface AttachmentChipProps {
  attachment: FileUIPart;
  size?: number;
  onRemove?: () => void;
  isUploading?: boolean;
  className?: string;
}

const FilePreview = memo(function FilePreview({
  fileUrl,
  mediaType,
  filename,
  onClose,
  pastedText,
}: {
  fileUrl: string;
  mediaType: string;
  filename: string;
  onClose: () => void;
  pastedText?: string;
}) {
  const isImage = isImageType(mediaType);
  const isPdf = isPdfType(mediaType);
  const isText = isTextType(mediaType);
  const [textContent, setTextContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isText) {
      if (pastedText) {
        setTextContent(pastedText);
      } else if (fileUrl) {
        setIsLoading(true);
        fetch(fileUrl)
          .then((res) => res.text())
          .then((text) => {
            setTextContent(text);
            setIsLoading(false);
          })
          .catch((err) => {
            console.error('Failed to load text file:', err);
            setTextContent('Failed to load file content');
            setIsLoading(false);
          });
      }
    }
  }, [isText, fileUrl, pastedText]);

  const dialogSize = isPdf ? 'xl' : isText ? 'lg' : 'md';

  return (
    <Dialog open={true} onClose={onClose} size={dialogSize}>
      {isText && <DialogTitle>{filename}</DialogTitle>}
      <DialogBody className={isImage ? 'p-4' : '!p-0'}>
        {isImage ? (
          <img
            src={fileUrl}
            alt={filename}
            className="max-w-full max-h-[85vh] object-contain mx-auto block"
          />
        ) : isPdf ? (
          <div className="h-[85vh]">
            <PdfViewer fileUrl={fileUrl} />
          </div>
        ) : isText ? (
          <div className="h-[85vh] flex flex-col">
            <div className="flex-1 overflow-auto p-6 bg-muted/30">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <LoadingSpinner size="lg" />
                </div>
              ) : (
                <pre className="font-mono text-sm text-foreground whitespace-pre-wrap break-words">
                  {textContent}
                </pre>
              )}
            </div>
          </div>
        ) : null}
      </DialogBody>
    </Dialog>
  );
});

export const AttachmentChip = memo<AttachmentChipProps>(function AttachmentChip({
  attachment,
  size,
  onRemove,
  isUploading = false,
  className,
}) {
  const Icon = getFileTypeIcon(attachment.mediaType);
  const isImage = isImageType(attachment.mediaType);
  const isPdf = isPdfType(attachment.mediaType);
  const isText = isTextType(attachment.mediaType);
  const filename = attachment.filename || 'file';
  const [showPreview, setShowPreview] = useState(false);

  const pastedText = 'pastedText' in attachment ? (attachment.pastedText as string) : undefined;
  const canPreview = (isImage || isPdf || isText) && (attachment.url || pastedText);

  const handleChipClick = () => {
    if (!isUploading && canPreview) {
      setShowPreview(true);
    }
  };

  return (
    <>
      <Badge
        color="gray"
        className={cn(
          '!rounded',
          canPreview && 'cursor-pointer',
          isUploading && 'opacity-60',
          className
        )}
        onClick={handleChipClick}
      >
        {isUploading ? <LoadingSpinner size="xs" /> : <Icon data-slot="icon" />}
        <span className="font-medium">{filename}</span>
        {size !== undefined && (
          <span className="text-muted-foreground/50">{formatFileSize(size)}</span>
        )}
        {onRemove && !isUploading && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="-mr-0.5 rounded-full p-0.5 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Remove attachment"
          >
            <Close size={12} />
          </button>
        )}
      </Badge>
      {showPreview && canPreview && (
        <FilePreview
          fileUrl={attachment.url}
          mediaType={attachment.mediaType}
          filename={filename}
          onClose={() => setShowPreview(false)}
          pastedText={pastedText}
        />
      )}
    </>
  );
});
